export class ResponseError extends Error {
    public response: Response;

    constructor(response: Response) {
        super(response.statusText);
        this.response = response;
    }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */

export function checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
        return response;
    }
    return response.json().then((res) => {
        const error = new ResponseError(response.statusText);
        error.response = res;
        throw error;
    });
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
export function parseJSON(response) {
    if (response.status === 204 || response.status === 205) {
        return null;
    }
    return response.json();
}

/**
 * Parses the text returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed Text from the request
 */
export function parseText(response) {
    if (response.status === 204 || response.status === 205) {
        return null;
    }
    return response.text();
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @param  {boolean} [isJSON] Choose if text or JSON response
 * @param  {boolean} [needsToken] In case of custom requests with/without token
 * @param  {boolean} [needsValidation] Enable/disable response validation
 *
 * @return {object}           The response data
 */
export default async function request(
    url: string,
    options?: any,
    isJSON = true,
    needsValidation = true,
): Promise<{} | { err: ResponseError }> {
    const parameters = options;
    const fetchResponse = await fetch(url, parameters);

    if (needsValidation) {
        const response = await checkStatus(fetchResponse);
        if (isJSON) {
            return parseJSON(response);
        }

        return parseText(response);
    }

    return fetchResponse;
}
